import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';

import API_BASE_URL from '../../../../Constants';

/**
 * ViewInspectorModal
 *
 * Shows all Inspector fields, with two <select> dropdowns:
 *   - employeeId -> loaded from /api/admin/users
 *   - serviceCompanyId -> loaded from /api/inspections/service-companies
 *
 * Props:
 *  - open: boolean
 *  - onClose: function
 *  - inspectorData: the inspector object
 *  - refreshInspectors: optional callback to reload the list
 */
const ViewInspectorModal = ({
  open,
  onClose,
  inspectorData,
  refreshInspectors,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [inspector, setInspector] = useState(null);

  // For populating the dropdowns
  const [employees, setEmployees] = useState([]);
  const [serviceCompanies, setServiceCompanies] = useState([]);

  // Basic validation errors
  const [errors, setErrors] = useState({});

  // 1) When modal opens, fetch employees + service companies
  useEffect(() => {
    if (open) {
      setInspector(inspectorData || {});
      setIsEditMode(false);
      setErrors({});

      // Fetch all employees
      fetch(`${API_BASE_URL}/api/admin/users`, {
        credentials: 'include',
      })
        .then((resp) => {
          if (!resp.ok) {
            throw new Error('Failed to fetch employees');
          }
          return resp.json();
        })
        .then((data) => {
          // 'data' should be an array of user objects
          setEmployees(data || []);
        })
        .catch((err) => {
          console.error('Error fetching employees:', err);
          setEmployees([]);
        });

      // Fetch all service companies
      fetch(`${API_BASE_URL}/api/inspections/service-companies`, {
        credentials: 'include',
      })
        .then((resp) => {
          if (!resp.ok) {
            throw new Error('Failed to fetch service companies');
          }
          return resp.json();
        })
        .then((data) => {
          // 'data' should be an array of serviceCompanyDTO
          setServiceCompanies(data || []);
        })
        .catch((err) => {
          console.error('Error fetching service companies:', err);
          setServiceCompanies([]);
        });
    }
  }, [open, inspectorData]);

  const handleClose = () => {
    onClose();
  };

  // 2) Toggling edit mode
  const toggleEdit = () => {
    if (!isEditMode) {
      setIsEditMode(true);
    } else {
      handleSave();
    }
  };

  // 3) Handle changes for text, checkboxes, and selects
  const handleChange = (e) => {
    if (!inspector) return;
    const { name, value, type, checked } = e.target;
    setInspector((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // Clear error
  };

  // 4) Saving updates (PUT)
  const handleSave = async () => {
    if (!inspector) return;

    if (!inspector.inspectorName?.trim()) {
      setErrors({ inspectorName: 'Inspector Name is required.' });
      alert('Please provide the inspector name.');
      return;
    }

    try {
      const url = `${API_BASE_URL}/api/inspectors/${inspector.id}`;
      const resp = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inspector),
      });
      if (!resp.ok) {
        throw new Error(`Failed to update inspector. Status: ${resp.status}`);
      }
      alert('Inspector updated successfully!');
      setIsEditMode(false);
      if (refreshInspectors) refreshInspectors();
    } catch (err) {
      console.error('Error updating inspector:', err);
      alert('Failed to update inspector.');
    }
  };

  // 5) Deleting record (DELETE)
  const handleDelete = async () => {
    if (!inspector?.id) return;
    if (!window.confirm('Are you sure you want to delete this inspector?')) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/api/inspectors/${inspector.id}`;
      const resp = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!resp.ok) {
        throw new Error(`Failed to delete inspector. Status: ${resp.status}`);
      }
      alert('Inspector deleted successfully!');
      if (refreshInspectors) refreshInspectors();
      onClose();
    } catch (err) {
      console.error('Error deleting inspector:', err);
      alert('Failed to delete inspector.');
    }
  };

  if (!inspector) {
    // No data or still loading
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      // Make the modal wider
      maxWidth={false}
      PaperProps={{
        style: {
          width: '90%',        // use 90% of screen width
          maxWidth: '1200px',  // cap at 1200px
        },
      }}
    >
      {/* Sticky Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4">
          {/* Left side: back arrow + title */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold">
              {isEditMode ? 'Edit Inspector' : 'Inspector Details'}
            </h2>
          </div>

          {/* Right side: expand/shrink */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </button>
        </div>

        {/* Buttons row */}
        <div className="flex space-x-4 p-4">
          {isEditMode ? (
            <>
              <button
                onClick={() => setIsEditMode(false)}
                className="px-4 py-2 bg-gray-300 font-bold rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-yellow-400 text-blue-700 font-bold rounded-md hover:bg-yellow-500"
              >
                Save
              </button>
            </>
          ) : (
            <>
              <button
                onClick={toggleEdit}
                className="px-4 py-2 bg-yellow-400 text-blue-700 font-bold rounded-md hover:bg-yellow-500"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <DialogContent dividers>
        <div className="grid grid-cols-2 gap-4">
          {/* ============ LEFT COLUMN ============ */}
          {/* InspectorID (read-only) */}
          <div>
            <label className="block font-semibold">Inspector ID (PK)</label>
            <p>{inspector.id ?? 'N/A'}</p>
          </div>

          {/* InspectorName */}
          <div>
            <label className="block font-semibold text-red-600">
              Inspector Name
            </label>
            {isEditMode ? (
              <input
                type="text"
                name="inspectorName"
                className={`border p-2 w-full rounded ${
                  errors.inspectorName ? 'border-red-500' : ''
                }`}
                value={inspector.inspectorName || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.inspectorName || 'N/A'}</p>
            )}
            {errors.inspectorName && (
              <p className="text-red-500">{errors.inspectorName}</p>
            )}
          </div>

          {/* EmployeeId -> Select from employees */}
          <div>
            <label className="block font-semibold">Employee ID</label>
            {isEditMode ? (
              <select
                name="employeeId"
                className="border p-2 w-full rounded"
                value={inspector.employeeId || ''}
                onChange={handleChange}
              >
                <option value="">-- Select Employee --</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {/* Show "ID - firstName lastName" or any format */}
                    {emp.id + ' - ' + emp.firstName + ' ' + emp.lastName}
                  </option>
                ))}
              </select>
            ) : (
              <p>{inspector.employeeId || 'N/A'}</p>
            )}
          </div>

          {/* ServiceCompanyId -> Select from serviceCompanies */}
          <div>
            <label className="block font-semibold">Service Company ID</label>
            {isEditMode ? (
              <select
                name="serviceCompanyId"
                className="border p-2 w-full rounded"
                value={inspector.serviceCompanyId || ''}
                onChange={handleChange}
              >
                <option value="">-- Select Company --</option>
                {serviceCompanies.map((sc) => (
                  <option key={sc.serviceCompanyId} value={sc.serviceCompanyId}>
                    {/* e.g. "ID - companyName" */}
                    {sc.serviceCompanyId + ' - ' + sc.companyName}
                  </option>
                ))}
              </select>
            ) : (
              <p>{inspector.serviceCompanyId || 'N/A'}</p>
            )}
          </div>

          {/* ServiceCompanyType */}
          <div>
            <label className="block font-semibold">Service Company Type</label>
            {isEditMode ? (
              <input
                type="text"
                name="serviceCompanyType"
                className="border p-2 w-full rounded"
                value={inspector.serviceCompanyType || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.serviceCompanyType || 'N/A'}</p>
            )}
          </div>

          {/* certificationNumber */}
          <div>
            <label className="block font-semibold">Certification #</label>
            {isEditMode ? (
              <input
                type="text"
                name="certificationNumber"
                className="border p-2 w-full rounded"
                value={inspector.certificationNumber || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.certificationNumber || 'N/A'}</p>
            )}
          </div>

          {/* IsDirector (bit) */}
          <div>
            <label className="block font-semibold">Is Director?</label>
            {isEditMode ? (
              <input
                type="checkbox"
                name="isDirector"
                checked={inspector.isDirector || false}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.isDirector ? 'Yes' : 'No'}</p>
            )}
          </div>

          {/* CompanyID */}
          <div>
            <label className="block font-semibold">Company ID (Legacy Field)</label>
            {isEditMode ? (
              <input
                type="text"
                name="companyId"
                className="border p-2 w-full rounded"
                value={inspector.companyId || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.companyId || 'N/A'}</p>
            )}
          </div>

          {/* ============ RIGHT COLUMN ============ */}
          {/* CertificationType */}
          <div>
            <label className="block font-semibold">Certification Type</label>
            {isEditMode ? (
              <input
                type="text"
                name="certificationType"
                className="border p-2 w-full rounded"
                value={inspector.certificationType || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.certificationType || 'N/A'}</p>
            )}
          </div>

          {/* CertificationLevel */}
          <div>
            <label className="block font-semibold">Certification Level</label>
            {isEditMode ? (
              <input
                type="text"
                name="certificationLevel"
                className="border p-2 w-full rounded"
                value={inspector.certificationLevel || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.certificationLevel || 'N/A'}</p>
            )}
          </div>

          {/* CertifiedBy */}
          <div>
            <label className="block font-semibold">Certified By</label>
            {isEditMode ? (
              <input
                type="text"
                name="certifiedBy"
                className="border p-2 w-full rounded"
                value={inspector.certifiedBy || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.certifiedBy || 'N/A'}</p>
            )}
          </div>

          {/* InspectorStatus */}
          <div>
            <label className="block font-semibold">Inspector Status</label>
            {isEditMode ? (
              <input
                type="text"
                name="inspectorStatus"
                className="border p-2 w-full rounded"
                value={inspector.inspectorStatus || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.inspectorStatus || 'N/A'}</p>
            )}
          </div>

          {/* CertificationDate */}
          <div>
            <label className="block font-semibold">Certification Date</label>
            {isEditMode ? (
              <input
                type="date"
                name="certificationDate"
                className="border p-2 w-full rounded"
                value={inspector.certificationDate || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.certificationDate || 'N/A'}</p>
            )}
          </div>

          {/* ExpirationDate */}
          <div>
            <label className="block font-semibold">Expiration Date</label>
            {isEditMode ? (
              <input
                type="date"
                name="expirationDate"
                className="border p-2 w-full rounded"
                value={inspector.expirationDate || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.expirationDate || 'N/A'}</p>
            )}
          </div>

          {/* DOBNowPassword */}
          <div>
            <label className="block font-semibold">DOB Now Password</label>
            {isEditMode ? (
              <input
                type="text"
                name="dobNowPassword"
                className="border p-2 w-full rounded"
                value={inspector.dobNowPassword || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.dobNowPassword || 'N/A'}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block font-semibold">Email</label>
            {isEditMode ? (
              <input
                type="email"
                name="email"
                className="border p-2 w-full rounded"
                value={inspector.email || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.email || 'N/A'}</p>
            )}
          </div>

          {/* OtherEmail */}
          <div>
            <label className="block font-semibold">Other Email</label>
            {isEditMode ? (
              <input
                type="email"
                name="otherEmail"
                className="border p-2 w-full rounded"
                value={inspector.otherEmail || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.otherEmail || 'N/A'}</p>
            )}
          </div>

          {/* RemoteDevice */}
          <div>
            <label className="block font-semibold">Remote Device</label>
            {isEditMode ? (
              <input
                type="text"
                name="remoteDevice"
                className="border p-2 w-full rounded"
                value={inspector.remoteDevice || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.remoteDevice || 'N/A'}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block font-semibold">Phone</label>
            {isEditMode ? (
              <input
                type="text"
                name="phone"
                className="border p-2 w-full rounded"
                value={inspector.phone || ''}
                onChange={handleChange}
              />
            ) : (
              <p>{inspector.phone || 'N/A'}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewInspectorModal;
